import { cloneDeepWith, mapKeys, mapValues } from 'lodash'
import pako from 'pako';

const referenceTag = '___js-to-json-reference___';
const pointerTag = '___js-to-json-pointer___';
const arrayReferenceTag = '___js-to-json-array-reference___';

let referenceId = 0;
const MAX_SAFE_INTEGER = Math.pow(2, 53) - 1;
function nextReferenceId() {
  if (referenceId === MAX_SAFE_INTEGER) {
    referenceId = 0;
  }
  return ++referenceId;
}

function ___Reference___(block) {
  let value = null
  this.deref = function() {
    if (value) return value
    value = block()
    return value
  }
}

export function toJSON(obj, options) {
  const arrays = []

  const result = cloneDeepWith(obj, (value, index, object, stack) => {
    const stacked = stack && stack.get(value);
    if (!stacked && Array.isArray(value)) {
      arrays.push({wrap: function(){
        const array = stack.get(value)
        if (array[referenceTag]) {
          stack.get(object)[index] = {
            [arrayReferenceTag]: array[referenceTag], 
            array: array,
          }
          delete array[referenceTag]
        }
      }})
    }

    if (stacked) {
      stacked[referenceTag] =
        stacked[referenceTag] || `REF_${nextReferenceId()}`;
      return { [pointerTag]: stacked[referenceTag] };
    }
  });
  arrays.forEach(({ wrap } ) => wrap())
  return result
}

export function fromJSON(obj) {
  const references = {};
  const pointers = [];
  const result = cloneDeepWith(obj, (value, index, object, stack) => {
    if (value) {
      if (value[pointerTag]) {
        pointers.push({
          pointer: value[pointerTag],
          object: stack.get(object),
          index,
        });
      } if (
        value[arrayReferenceTag]
      ) {
        references[value[arrayReferenceTag]] = new ___Reference___(
            function(){
              const array = stack.get(value).array
              stack.get(object)[index] = array
              return array
            }
          )
      } else if (index === referenceTag) {
        references[value] = stack.get(object) 
      }
    }
  });
  pointers.forEach(({ pointer, object, index }) => {
    const reference = references[pointer]
    if (reference instanceof ___Reference___) {
      object[index] = reference.deref()
    } else {
      object[index] = reference;
      delete references[pointer][referenceTag];
    }
  });
  return result; 
}

export function fullDry(obj) {
  const referenceToValue = new Map()
  const valueToReference = new Map()
  let currentValueReference = 1

  const referenceToKey = new Map()
  const keyToReference = new Map()
  let currentKeyReference = 0

  function helper(value) {
    if (Array.isArray(value)) {
      const result = value.map((o)=>{return helper(o)})
      if (value[referenceTag]) {
        result[referenceTag] = value[referenceTag]
      }
      return result
    } else
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      return mapKeys(
        mapValues(value, (o) => {return helper(o)}),
        (value, key) => {
          let reference = keyToReference.get(key)
          if (!reference) {
            reference = currentKeyReference
            currentKeyReference = currentKeyReference + 1
            referenceToKey.set(reference, key)
            keyToReference.set(key, reference)
          }
          return reference;
        }
      )
    } else {
      if (value === undefined) {
        return String.fromCharCode(0)
      } else 
      if (typeof value === 'string') {
        let reference = valueToReference.get(value)
        if (!reference) {
          reference = String.fromCharCode(currentValueReference)
          currentValueReference = currentValueReference + 1
          valueToReference.set(value, reference)
          referenceToValue.set(reference, value)
        }
        return reference
      } else {
        return value
      }
    }
  }

  const result = helper(obj)

  return {
    references: Array.from(referenceToValue),
    keys: Array.from(referenceToKey),
    obj: result
  }
}

export function fullUndry({ references, keys, obj }){
  const referenceToValue = new Map(references)
  const referenceToKey = new Map(keys)

  const pointers = []
  const objectReferences = new Map()

  function helper(value, key, object){
    if (Array.isArray(value)) {
      const result = []
      value.forEach((o, i) => {
        result[i] = helper(o, i, result)
      })
      if (value[referenceTag]) {
        result[referenceTag] = value[referenceTag]
      }
      return result
    } else
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      const newValue = {}
      for (const reference in value) {
        if (value.hasOwnProperty(reference)) {
          const dereferedKey = referenceToKey.get(Number(reference))
          newValue[dereferedKey] = helper(value[reference], dereferedKey, newValue)
        }
      }
      if (newValue.hasOwnProperty(pointerTag)) {
        
        pointers.push(function(){
          object[key] = objectReferences.get(newValue[pointerTag])
          delete objectReferences.get(newValue[pointerTag])[referenceTag]
        })
      }
      if (newValue.hasOwnProperty(arrayReferenceTag)) {
        object[key] = newValue.array
        objectReferences.set(newValue[arrayReferenceTag], newValue.array)//[newValue[arrayReferenceTag]] = newValue.array
      } else
      if (newValue.hasOwnProperty(referenceTag)) {
        objectReferences.set(newValue[referenceTag], newValue)//[newValue[referenceTag]] = newValue
      }
      return newValue
    } else {
      if (value === String.fromCharCode(0)) {
        return undefined
      } else if (typeof value === 'string') {
        return referenceToValue.get(value)
      } else {
        return value
      }
    }
  }

  const result = helper(obj)

  pointers.forEach((pointer) => pointer())

  return result
}

export function _arrayBufferToBase64( buffer ) {
  var binary = '';
  var bytes = new Uint8Array( buffer );
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
      binary += String.fromCharCode( bytes[ i ] );
  }
  return window.btoa( binary );
}

export function _base64ToArrayBuffer(base64) {
  var binary_string = window.atob(base64);
  var len = binary_string.length;
  var bytes = new Uint8Array(len);
  for (var i = 0; i < len; i++) {
      bytes[i] = binary_string.charCodeAt(i);
  }
  return bytes.buffer;
}

export function serialize(state, needExportToJSON = true) {
  var t0 = performance.now()
  const compressed = 
    _arrayBufferToBase64(
      pako.deflate(
        JSON.stringify(
          fullDry(
            toJSON(
              state
            )
          )
        )
      )
    )
  var t1 = performance.now();
  return compressed
}

export function parseArray(state){
  try {
    return _base64ToArrayBuffer(state)
  } catch (error) {
    return state.split(',').map(x => +x)
  }
 }

export function deserialize(compressed) {
  var t0 = performance.now()
  const buffer   = parseArray(compressed)
  var t1 = performance.now()
  const inflated = new Uint8Array(pako.inflate(buffer))
  var t2 = performance.now()
  const decoded = (new TextDecoder).decode(inflated)
  var t3 = performance.now()
  const json = JSON.parse(decoded)
  const t4 = performance.now()
  let result = json
  let t5 = t4
  let t6 = t5
  if (json.references && json.keys) {
    const undried = fullUndry(json)
    t5 = performance.now()
    result = undried
    t6 = t5
  }
  return result
}